"use client";

import { prepareMembershipBilling } from "@/actions/commerce-billing";
import { confirmRazorpaySubscriptionPayment } from "@/actions/tier";
import { PAYMENT_CONFIRMATION_PENDING_MESSAGE } from "@/features/payments/lib/messages";
import { createClientLogger } from "@/lib/logging/client";
import {
  beginNativePaymentNavigation,
  endNativePaymentNavigation,
  isNativePaymentShell,
} from "@/lib/native/payment-navigation";
import { getPublicAppOrigin } from "@/lib/public-app-url";
import { openRazorpayCheckout } from "@/lib/razorpay-checkout";

const logger = createClientLogger("lib/razorpay-subscription-checkout");

export type RazorpaySubscriptionCheckoutPayload = {
  provider: "razorpay";
  keyId: string;
  subscriptionId: string;
  membershipId: string;
  hiiveSlug: string;
  amountMinor: number;
  currency: string;
  name: string;
  description: string;
  prefill: {
    name?: string;
    email?: string;
    contact?: string;
  };
};

export function getRazorpaySubscriptionCheckout(
  value: unknown,
): RazorpaySubscriptionCheckoutPayload | null {
  if (!value || typeof value !== "object" || !("checkout" in value)) {
    return null;
  }

  const checkout = (value as { checkout?: unknown }).checkout;
  if (!checkout || typeof checkout !== "object") {
    return null;
  }

  const candidate = checkout as Partial<RazorpaySubscriptionCheckoutPayload>;
  if (
    candidate.provider !== "razorpay" ||
    !candidate.keyId ||
    !candidate.subscriptionId ||
    !candidate.membershipId ||
    !candidate.hiiveSlug
  ) {
    return null;
  }

  return candidate as RazorpaySubscriptionCheckoutPayload;
}

export function getRazorpaySubscriptionReturnUrl(
  checkout: Pick<
    RazorpaySubscriptionCheckoutPayload,
    "membershipId" | "hiiveSlug"
  >,
) {
  if (typeof window === "undefined") return undefined;
  const url = new URL(
    "/api/razorpay/membership-subscription-return",
    getPublicAppOrigin(),
  );
  url.searchParams.set("membershipId", checkout.membershipId);
  url.searchParams.set("hiiveSlug", checkout.hiiveSlug);
  return url.toString();
}

export async function completeRazorpaySubscriptionCheckout(
  checkout: RazorpaySubscriptionCheckoutPayload,
) {
  const billing = await prepareMembershipBilling(checkout.membershipId);
  if (!billing.ok) return billing;
  let nativeNavigationOpen = false;
  let providerReturnedPayment = false;
  try {
    try {
      nativeNavigationOpen = await beginNativePaymentNavigation();
    } catch (error) {
      logger.warn("Could not open native payment navigation lease", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
    const returnUrl = getRazorpaySubscriptionReturnUrl(checkout);
    const useCallback = nativeNavigationOpen && Boolean(returnUrl);
    const result = await openRazorpayCheckout({
      key: checkout.keyId,
      subscription_id: checkout.subscriptionId,
      name: checkout.name,
      description: checkout.description,
      ...(useCallback && returnUrl ? { callback_url: returnUrl } : {}),
      redirect: useCallback,
      webview_intent: isNativePaymentShell(),
      prefill: checkout.prefill,
      notes: {
        membershipId: checkout.membershipId,
        subscriptionId: checkout.subscriptionId,
      },
      retry: { enabled: false },
      theme: { color: "#6a33ff" },
    });

    if (!result.ok) {
      return result;
    }
    providerReturnedPayment = true;

    const subscriptionId = result.data.razorpay_subscription_id;
    if (!subscriptionId) {
      return {
        ok: false as const,
        message: "Razorpay did not return a subscription id.",
      };
    }

    const confirmed = await confirmRazorpaySubscriptionPayment({
      membershipId: checkout.membershipId,
      razorpayPaymentId: result.data.razorpay_payment_id,
      razorpaySubscriptionId: subscriptionId,
      razorpaySignature: result.data.razorpay_signature,
    });
    if (!confirmed.ok || !confirmed.data?.confirmed) {
      return {
        ok: false as const,
        message: PAYMENT_CONFIRMATION_PENDING_MESSAGE,
      };
    }
    return confirmed;
  } catch (error) {
    logger.error("Subscription checkout threw", {
      error: error instanceof Error ? error.message : String(error),
      providerReturnedPayment,
    });
    return {
      ok: false as const,
      message: providerReturnedPayment
        ? PAYMENT_CONFIRMATION_PENDING_MESSAGE
        : "Couldn't start payment. Check your connection and try again.",
    };
  } finally {
    // A successful redirect unloads this document before `finally` can run.
    // The root app reset releases that stranded lease on the return landing.
    await endNativePaymentNavigation();
  }
}
